import React, { useState, useRef, useEffect } from 'react';
import { Loader2 } from 'lucide-react';
import ResponsiveImage from './ResponsiveImage';

/**
 * Componente immagine con preview al hover
 * Mostra una preview più grande dopo 2 secondi di hover
 */
const ImageWithPreview = ({ 
    src, 
    alt, 
    maxHeight = "max-h-64", 
    maxWidth = "w-full",
    previewSize = "w-96" // Dimensione della preview
}) => {
    const [showPreview, setShowPreview] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const hoverTimeoutRef = useRef(null);
    const previewRef = useRef(null);
    const [previewPosition, setPreviewPosition] = useState({ x: 0, y: 0 });

    // Gestisce l'inizio dell'hover
    const handleMouseEnter = (e) => {
        // Cancella eventuali timeout precedenti
        if (hoverTimeoutRef.current) {
            clearTimeout(hoverTimeoutRef.current);
        }

        // Imposta posizione iniziale basata sulla posizione del mouse
        const rect = e.currentTarget.getBoundingClientRect();
        setPreviewPosition({
            x: rect.right + 10, // A destra dell'immagine
            y: rect.top
        });

        // Dopo 0.5 secondi mostra la preview
        setIsLoading(true);
        hoverTimeoutRef.current = setTimeout(() => {
            setShowPreview(true);
            setIsLoading(false);
        }, 500);
    };

    // Gestisce la fine dell'hover
    const handleMouseLeave = () => {
        if (hoverTimeoutRef.current) {
            clearTimeout(hoverTimeoutRef.current);
            hoverTimeoutRef.current = null;
        }
        setIsLoading(false);
        setShowPreview(false);
    };

    // Aggiorna la posizione della preview quando il mouse si muove
    const handleMouseMove = (e) => {
        if (showPreview || isLoading) {
            const rect = e.currentTarget.getBoundingClientRect();
            setPreviewPosition({
                x: rect.right + 10,
                y: rect.top
            });
        }
    };

    // Cleanup al unmount
    useEffect(() => {
        return () => {
            if (hoverTimeoutRef.current) {
                clearTimeout(hoverTimeoutRef.current);
            }
        };
    }, []);

    // Gestisce il posizionamento della preview per evitare che esca dallo schermo
    useEffect(() => {
        if (showPreview && previewRef.current) {
            const preview = previewRef.current;
            const rect = preview.getBoundingClientRect();
            const viewportWidth = window.innerWidth;
            const viewportHeight = window.innerHeight;

            let x = previewPosition.x;
            let y = previewPosition.y;

            // Se la preview esce a destra, mostrala a sinistra
            if (x + rect.width > viewportWidth) {
                x = previewPosition.x - rect.width - 20;
            }

            // Se la preview esce in basso, spostala in alto
            if (y + rect.height > viewportHeight) {
                y = viewportHeight - rect.height - 10;
            }

            // Se la preview esce in alto, spostala in basso
            if (y < 0) {
                y = 10;
            }

            preview.style.left = `${x}px`;
            preview.style.top = `${y}px`;
        }
    }, [showPreview, previewPosition]);

    return (
        <>
            <div
                className="relative cursor-pointer"
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
                onMouseMove={handleMouseMove}
            >
                <ResponsiveImage
                    src={src}
                    alt={alt}
                    maxHeight={maxHeight}
                    maxWidth={maxWidth}
                />
                
                {/* Icona di caricamento durante l'attesa */}
                {isLoading && (
                    <div className="absolute inset-0 flex items-center justify-center bg-black/20 rounded-lg backdrop-blur-sm">
                        <Loader2 className="h-8 w-8 text-white animate-spin" />
                    </div>
                )}
            </div>

            {/* Preview più grande */}
            {showPreview && (
                <div
                    ref={previewRef}
                    className={`fixed ${previewSize} z-50 pointer-events-none transition-opacity duration-200`}
                    style={{
                        left: `${previewPosition.x}px`,
                        top: `${previewPosition.y}px`,
                    }}
                >
                    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-2xl border-2 border-gray-200 dark:border-gray-700 overflow-hidden">
                        <ResponsiveImage
                            src={src}
                            alt={alt}
                            maxHeight="max-h-96"
                            maxWidth="w-full"
                        />
                    </div>
                </div>
            )}
        </>
    );
};

export default ImageWithPreview;
